import axios from "axios";
import dayjs from "dayjs";
import { EventEmitter } from "events";
import {
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import { toast } from "react-toastify";

const MissionContext = createContext();
const MissionEvents = new EventEmitter();

export const MissionProvider = ({ children }) => {
  const [missions, setMissions] = useState([]);
  const [activeStatusStats, setActiveStatusStats] = useState({});
  const [terminalStatusStats, setTerminalStatusStats] = useState({});
  const [statusStatsLoading, setStatusStatsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 200,
    total: 0,
    totalPages: 0,
  });
  const [searchParams, setSearchParams] = useState({
    search: "",
    status: "",
    partnerId: "",
    riderId: "",
    pickupLocation: "",
    startDate: null,
    endDate: null,
  });

  const updatePagination = (newPagination) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  };

  const updateSearchParams = (newParams) => {
    setSearchParams((prev) => ({ ...prev, ...newParams }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const getMissions = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        ...searchParams,
        page: pagination.page,
        limit: pagination.limit,
      };
      const response = await axios.get(`/missions`, { params });
      const { data, total, totalPages } = response.data;
      setMissions(data);
      setPagination((prev) => ({ ...prev, total, totalPages }));
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.message || error.message;
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [searchParams, pagination.page, pagination.limit]);

  // Mirror du pattern OrderContext.getStatusStats : snapshot pour le backlog actif
  // + funnel période pour les statuts terminaux. Période = aujourd'hui par défaut.
  // `status` et `search` sont volontairement exclus.
  const getStatusStats = useCallback(async () => {
    try {
      setStatusStatsLoading(true);
      const commonParams = {
        partnerId: searchParams.partnerId,
        riderId: searchParams.riderId,
        pickupLocation: searchParams.pickupLocation,
      };
      const today = dayjs().format("YYYY-MM-DD");
      const periodParams = {
        ...commonParams,
        startDate: searchParams.startDate || today,
        endDate: searchParams.endDate || today,
      };
      const [snapshotRes, periodRes] = await Promise.all([
        axios.get(`/stats/missions/snapshot`, { params: commonParams }),
        axios.get(`/stats/missions/period`, { params: periodParams }),
      ]);
      setActiveStatusStats(snapshotRes.data);
      setTerminalStatusStats(periodRes.data);
    } catch (error) {
      console.error("Mission status stats error:", error);
    } finally {
      setStatusStatsLoading(false);
    }
  }, [searchParams]);

  const getMissionById = async (missionId) => {
    try {
      const response = await axios.get(`/missions/${missionId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching mission by ID:", error);
      throw error;
    }
  };

  const startMission = async (missionId) => {
    try {
      const response = await axios.patch(`/missions/${missionId}/start`);
      MissionEvents.emit("mission:updated");
      return response.data;
    } catch (error) {
      console.error("Error starting mission:", error);
      throw error;
    }
  };

  const completeMission = async (missionId) => {
    try {
      const response = await axios.patch(`/missions/${missionId}/complete`);
      MissionEvents.emit("mission:updated");
      return response.data;
    } catch (error) {
      console.error("Error completing mission:", error);
      throw error;
    }
  };

  const cancelMission = async (missionId) => {
    try {
      const response = await axios.patch(`/missions/${missionId}/cancel`);
      MissionEvents.emit("mission:updated");
      return response.data;
    } catch (error) {
      console.error("Error canceling mission:", error);
      throw error;
    }
  };

  const cancelMissionItem = async (missionId, itemId) => {
    try {
      const response = await axios.patch(
        `/missions/${missionId}/items/${itemId}/cancel`
      );
      MissionEvents.emit("mission:updated");
      return response.data;
    } catch (error) {
      console.error("Error canceling mission item:", error);
      throw error;
    }
  };

  const value = {
    missions,
    activeStatusStats,
    terminalStatusStats,
    statusStatsLoading,
    loading,
    pagination,
    searchParams,
    getMissions,
    getStatusStats,
    getMissionById,
    startMission,
    completeMission,
    cancelMission,
    cancelMissionItem,
    updatePagination,
    updateSearchParams,
    MissionEvents,
  };

  return (
    <MissionContext.Provider value={value}>{children}</MissionContext.Provider>
  );
};

export const useMissionContext = () => {
  const context = useContext(MissionContext);
  if (!context) {
    throw new Error("useMissionContext must be used within a MissionProvider");
  }
  return context;
};
